import { WaypointStatusName, DbRef, WaypointActionType } from '@caresend/types';
import { deduplicateArray, nullishFilter, hasStatus, initElementTimestamp, getProcedurePath, getWaypointActionPath, arrayToObj, objectMap, getWaypointPath, getShipmentPath } from '@caresend/utils';
import { usePortalStore } from '../index.js';
import { useDbSyncStore } from '../../storeUtilities/dbSync/dbSyncStore.js';

/**
 * Create waypoint action reference on procedures
 *
 * @param waypointAction - Waypoint action to update procedure references for
 *
 * Example
 * ------
 * ### Input
 * `waypointAction {
 *   id: '123',
 *   procedures: {
 *     '1': { id: '1' },
 *     '2': { id: '2' },
 *   },
 * }`
 *
 * ### Output
 * `procedures {
 *   '1': {
 *     waypointActions: {
 *       '123': { id: '123' },
 *     },
 *   },
 *   '2': {
 *     waypointActions: {
 *       '123': { id: '123' },
 *     },
 *   },
 * }`
 */
const setWaypointActionProcedureReferences = (waypointAction) => {
    var _a;
    const portalStore = usePortalStore();
    const db = useDbSyncStore();
    const { itinerary } = portalStore;
    const { procedures } = waypointAction;
    if (!procedures)
        return;
    /** Compare against existing to find removed procedures */
    const existingWaypointAction = portalStore.waypointActions.getRaw(waypointAction.id);
    const removedProcedureIDs = Object.keys((_a = existingWaypointAction === null || existingWaypointAction === void 0 ? void 0 : existingWaypointAction.procedures) !== null && _a !== void 0 ? _a : {}).filter((procedureID) => { var _a; return !((_a = waypointAction.procedures) === null || _a === void 0 ? void 0 : _a[procedureID]); });
    Object.keys(procedures).forEach((procedureID) => {
        var _a;
        const procedure = portalStore.procedures.getRaw(procedureID);
        if (procedure) {
            const procedureWaypointIDs = deduplicateArray(Object.keys((_a = procedure.waypointActions) !== null && _a !== void 0 ? _a : {})
                .map((id) => { var _a; return (_a = portalStore.waypointActions.getRaw(id)) === null || _a === void 0 ? void 0 : _a.waypointID; })
                .filter(nullishFilter));
            const procedureWaypoints = procedureWaypointIDs
                .map((id) => portalStore.waypoints.get(id))
                .filter(nullishFilter);
            const anyWpsDrafts = procedureWaypoints.some((wps) => hasStatus(wps, WaypointStatusName.DRAFT));
            if (anyWpsDrafts)
                return;
        }
        if (itinerary.value.nurseID) {
            portalStore.procedures.setChildOf(procedureID, 'nurseID', itinerary.value.nurseID, 'set nurse ID on procedure');
        }
        portalStore.procedures.setChildOf(procedureID, `waypointActions/${waypointAction.id}`, initElementTimestamp(waypointAction.id), 'create waypoint action reference on procedure');
    });
    if (removedProcedureIDs.length > 0) {
        removedProcedureIDs.forEach((procedureID) => {
            var _a;
            const procedure = portalStore.procedures.get(procedureID);
            if (!((_a = procedure === null || procedure === void 0 ? void 0 : procedure.waypointActions) === null || _a === void 0 ? void 0 : _a[waypointAction.id]))
                return;
            db.queueNextUpdate(getProcedurePath(procedureID, `waypointActions/${waypointAction.id}`), 'delete waypoint action reference from procedure', null, DbRef.ITINERARIES);
            db.queueNextUpdate(getWaypointActionPath(waypointAction.id, `procedures/${procedureID}`), 'delete procedure reference from waypoint action', null, DbRef.ITINERARIES);
        });
    }
    /** No itinerary write changes required */
};
/**
 * For some set of waypoint actions, add references to waypoints
 * and sync with db
 */
const addWaypointActionsReferencesToWaypoints = (waypointActions) => {
    const portalStore = usePortalStore();
    const db = useDbSyncStore();
    /** Preprocess waypoint action references by waypoint ID */
    const waypointActionReferencesByWpID = {};
    waypointActions.forEach(({ waypointID }) => {
        var _a;
        if (!waypointID)
            return;
        /** Already initialized in this run */
        if (waypointActionReferencesByWpID[waypointID])
            return;
        const waypoint = portalStore.waypoints.get(waypointID);
        waypointActionReferencesByWpID[waypointID] = arrayToObj((_a = waypoint === null || waypoint === void 0 ? void 0 : waypoint.waypointActions) !== null && _a !== void 0 ? _a : [], 'id');
    });
    /** Update waypoint action references */
    waypointActions.forEach(({ id, waypointID, type }) => {
        if (!waypointID)
            return;
        const wpaReferences = waypointActionReferencesByWpID[waypointID];
        if (!wpaReferences || wpaReferences[id])
            return;
        const waypoint = portalStore.waypoints.get(waypointID);
        if (!waypoint)
            return;
        const isSampleProcessingAction = type === WaypointActionType.SAMPLE;
        wpaReferences[id] = { id };
        if (isSampleProcessingAction) {
            const processingActions = objectMap(wpaReferences, () => true);
            portalStore.waypoints.set({
                ...waypoint,
                processingActions,
            });
            db.queueNextUpdate(getWaypointPath(waypoint.id, 'waypointActions'), 'add processing action reference to waypoint', processingActions, DbRef.ITINERARIES);
        }
        else {
            const newWpas = Object.values(wpaReferences);
            portalStore.waypoints.set({
                ...waypoint,
                waypointActions: newWpas,
            });
            db.queueNextUpdate(getWaypointPath(waypoint.id, 'waypointActions'), 'add waypoint action reference to waypoint', newWpas, DbRef.ITINERARIES);
        }
    });
};
const commitAndDeleteDraftShipments = (wpa) => {
    if (wpa.type !== WaypointActionType.MAIL_IN)
        return;
    const portalStore = usePortalStore();
    const db = useDbSyncStore();
    const { shipments } = wpa;
    const shipmentIDs = Object.keys(shipments !== null && shipments !== void 0 ? shipments : {});
    shipmentIDs.forEach((shipmentID) => {
        const shipment = portalStore.draftShipments.get(shipmentID);
        if (!shipment)
            return;
        db.queueNextUpdate(getShipmentPath(shipmentID), 'create or update shipment', shipment, DbRef.ITINERARIES);
    });
    shipmentIDs.forEach((shipmentID) => {
        portalStore.draftShipments.unset(shipmentID);
    });
};

export { addWaypointActionsReferencesToWaypoints, commitAndDeleteDraftShipments, setWaypointActionProcedureReferences };
